import { LabelId, UserCommunication } from '@this+that/models';
import { StoreApi } from 'zustand';

import {
  CommunicationIntent,
  CommunicationIntentIdentity,
  CommunicationIntentKind,
  CommunicationIntentsStore,
  ReconcileCommunicationResult,
  SetLabelsIntentParams,
} from './types';
import {
  COMMUNICATION_INTENT_SATISFIED_GRACE_MS,
  COMMUNICATION_INTENT_TTL_MS,
} from '../../../utils/constants/communicationIntents';
import { hasLinkedDoBox } from '../../../utils/userCommunicationHelpers';

type ZustandSet = StoreApi<CommunicationIntentsStore>['setState'];
type ZustandGet = StoreApi<CommunicationIntentsStore>['getState'];

export const isIntentExpired = (
  intent: CommunicationIntent,
  now: number = Date.now(),
): boolean =>
  now - (intent.expiresFrom ?? intent.createdAt) > COMMUNICATION_INTENT_TTL_MS;

export const isIntentActive = (
  intent: CommunicationIntent,
  now: number = Date.now(),
): boolean => {
  if (isIntentExpired(intent, now)) {
    return false;
  }
  return (
    intent.satisfiedAt === undefined ||
    now - intent.satisfiedAt <= COMMUNICATION_INTENT_SATISFIED_GRACE_MS
  );
};

// After the TTL, server truth wins even if it still disagrees — a still-wrong
// value past this point is a real backend failure, not a sync lag, and hiding
// it forever would just recreate the bug this store replaces.
export const isViewHiddenIntent = (
  intent: CommunicationIntent | undefined,
  now: number = Date.now(),
): boolean => {
  if (!intent || isIntentExpired(intent, now)) {
    return false;
  }
  return intent.kind === CommunicationIntentKind.SNOOZED;
};

export const computeLabelIdsDelta = (
  originalLabelIds: LabelId[] | undefined,
  expectedLabelIds: LabelId[],
): { addLabelIds: LabelId[]; removeLabelIds: LabelId[] } => {
  const original = new Set(originalLabelIds ?? []);
  const expected = new Set(expectedLabelIds);
  return {
    addLabelIds: expectedLabelIds.filter((id) => !original.has(id)),
    removeLabelIds: (originalLabelIds ?? []).filter((id) => !expected.has(id)),
  };
};

// Delta comparison, never array equality: the server is free to legitimately
// flip UNREAD/IMPORTANT/STARRED mid-flight, and that must not invalidate the
// intent we're waiting on.
export const isLabelsIntentSatisfied = (
  intent: CommunicationIntent,
  incomingLabelIds: LabelId[] | undefined,
): boolean => {
  const incoming = new Set(incomingLabelIds ?? []);
  const removedOk = (intent.removeLabelIds ?? []).every(
    (id) => !incoming.has(id),
  );
  const addedOk = (intent.addLabelIds ?? []).every((id) => incoming.has(id));
  return removedOk && addedOk;
};

export const applyLabelsIntentCorrection = (
  intent: CommunicationIntent,
  incomingLabelIds: LabelId[] | undefined,
): LabelId[] => {
  const withoutRemoved = (incomingLabelIds ?? []).filter(
    (id) => !(intent.removeLabelIds ?? []).includes(id),
  );
  return Array.from(
    new Set([...withoutRemoved, ...(intent.addLabelIds ?? [])]),
  );
};

export const isLinkedToEntryIntentSatisfied = (
  communication: Pick<UserCommunication, 'inboxEntryIds'>,
): boolean => hasLinkedDoBox(communication);

// THREAD_FALLBACK matches are intentional for CORRECTING a delayed/aggregated
// payload, but must never SATISFY the intent: an old sibling message in the
// same thread can trivially already lack the labels being removed, which
// would stop the intent from correcting the real target message later.
enum CommunicationIntentMatchKind {
  MESSAGE_ID = 'messageId',
  INTERNET_MESSAGE_ID = 'internetMessageId',
  THREAD_FALLBACK = 'threadFallback',
}

interface CommunicationIntentMatch {
  intent: CommunicationIntent;
  matchKind: CommunicationIntentMatchKind;
}

const findIntentForCommunication = (
  intents: Record<string, CommunicationIntent>,
  communication: Pick<
    UserCommunication,
    'messageId' | 'internetMessageId' | 'threadMessageId' | 'createdAt'
  >,
): CommunicationIntentMatch | undefined => {
  const direct = intents[communication.messageId];
  if (direct) {
    return {
      intent: direct,
      matchKind: CommunicationIntentMatchKind.MESSAGE_ID,
    };
  }
  // The Graph API rotates messageId on folder moves; internetMessageId is the
  // stable anchor the intent was also recorded under.
  const byInternetMessageId = communication.internetMessageId
    ? Object.values(intents).find(
        (intent) =>
          intent.internetMessageId === communication.internetMessageId,
      )
    : undefined;
  if (byInternetMessageId) {
    return {
      intent: byInternetMessageId,
      matchKind: CommunicationIntentMatchKind.INTERNET_MESSAGE_ID,
    };
  }
  if (!communication.threadMessageId) {
    return undefined;
  }
  const byThread = Object.values(intents).find(
    (intent) =>
      intent.threadMessageId === communication.threadMessageId &&
      // A newer createdAt means a real reply landed — don't let a stale thread match hide it.
      communication.createdAt <= intent.createdAt,
  );
  return byThread
    ? {
        intent: byThread,
        matchKind: CommunicationIntentMatchKind.THREAD_FALLBACK,
      }
    : undefined;
};

export const findActiveLabelsIntentFor = (
  intents: Record<string, CommunicationIntent>,
  communication: Pick<
    UserCommunication,
    'messageId' | 'internetMessageId' | 'threadMessageId' | 'createdAt'
  >,
  now?: number,
): CommunicationIntent | undefined => {
  const match = findIntentForCommunication(intents, communication);
  if (
    !match ||
    match.intent.kind !== CommunicationIntentKind.LABELS ||
    !isIntentActive(match.intent, now)
  ) {
    return undefined;
  }
  return match.intent;
};

const buildIntent = (
  kind: CommunicationIntentKind,
  identity: CommunicationIntentIdentity,
  labelFields?: { addLabelIds: LabelId[]; removeLabelIds: LabelId[] },
): CommunicationIntent => {
  const createdAt = Date.now();
  return {
    ...identity,
    kind,
    ...labelFields,
    createdAt,
    expiresFrom: createdAt,
  };
};

export const handleSetLabelsIntent = (
  set: ZustandSet,
  params: SetLabelsIntentParams,
) => {
  const { addLabelIds, removeLabelIds } = computeLabelIdsDelta(
    params.originalLabelIds,
    params.expectedLabelIds,
  );

  set((state) => ({
    intents: {
      ...state.intents,
      [params.messageId]: buildIntent(CommunicationIntentKind.LABELS, params, {
        addLabelIds,
        removeLabelIds,
      }),
    },
  }));
};

export const handleSetSnoozedIntent = (
  set: ZustandSet,
  params: CommunicationIntentIdentity,
) => {
  set((state) => ({
    intents: {
      ...state.intents,
      [params.messageId]: buildIntent(CommunicationIntentKind.SNOOZED, params),
    },
  }));
};

export const handleSetLinkedToEntryIntent = (
  set: ZustandSet,
  params: CommunicationIntentIdentity,
) => {
  set((state) => ({
    intents: {
      ...state.intents,
      [params.messageId]: buildIntent(
        CommunicationIntentKind.LINKED_TO_ENTRY,
        params,
      ),
    },
  }));
};

export const handleClearIntent = (set: ZustandSet, messageId: string) => {
  set((state) => {
    if (!(messageId in state.intents)) {
      return state;
    }
    const nextIntents = { ...state.intents };
    delete nextIntents[messageId];
    return { intents: nextIntents };
  });
};

export const handleClearIntents = (
  set: ZustandSet,
  get: ZustandGet,
  messageIds: string[],
) => {
  if (messageIds.length === 0) {
    return;
  }
  const { intents } = get();
  if (!messageIds.some((messageId) => messageId in intents)) {
    return;
  }
  set((state) => {
    const nextIntents = { ...state.intents };
    messageIds.forEach((messageId) => delete nextIntents[messageId]);
    return { intents: nextIntents };
  });
};

const markIntentSatisfied = (set: ZustandSet, messageId: string) => {
  set((state) => {
    const intent = state.intents[messageId];
    if (!intent) {
      return state;
    }
    return {
      intents: {
        ...state.intents,
        [messageId]: { ...intent, satisfiedAt: Date.now() },
      },
    };
  });
};

const reconcileLabelsIntent = (
  set: ZustandSet,
  intent: CommunicationIntent,
  communication: UserCommunication,
  matchKind: CommunicationIntentMatchKind,
): ReconcileCommunicationResult => {
  const passthrough = { communication, wasCorrected: false };

  if (intent.satisfiedAt !== undefined) {
    return passthrough;
  }

  if (isLabelsIntentSatisfied(intent, communication.labelIds)) {
    if (matchKind !== CommunicationIntentMatchKind.THREAD_FALLBACK) {
      markIntentSatisfied(set, intent.messageId);
    }
    return passthrough;
  }

  return {
    communication: {
      ...communication,
      labelIds: applyLabelsIntentCorrection(intent, communication.labelIds),
    },
    wasCorrected: true,
  };
};

export const handleReconcileCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  communication: UserCommunication,
): ReconcileCommunicationResult => {
  const passthrough = { communication, wasCorrected: false };

  const match = findIntentForCommunication(get().intents, communication);
  if (!match) {
    return passthrough;
  }
  const { intent, matchKind } = match;

  if (!isIntentActive(intent)) {
    handleClearIntent(set, intent.messageId);
    return passthrough;
  }

  if (intent.kind === CommunicationIntentKind.LABELS) {
    return reconcileLabelsIntent(set, intent, communication, matchKind);
  }

  if (intent.kind === CommunicationIntentKind.LINKED_TO_ENTRY) {
    if (isLinkedToEntryIntentSatisfied(communication)) {
      handleClearIntent(set, intent.messageId);
    }
    return passthrough;
  }

  return passthrough;
};

export const handleReconcileSnoozedIntents = (
  set: ZustandSet,
  get: ZustandGet,
  snoozedMessageIds: string[],
) => {
  const snoozed = new Set(snoozedMessageIds);
  const toClear = Object.values(get().intents)
    .filter(
      (intent) =>
        intent.kind === CommunicationIntentKind.SNOOZED &&
        snoozed.has(intent.messageId),
    )
    .map((intent) => intent.messageId);

  handleClearIntents(set, get, toClear);
};

export const handleSettleIntent = (
  set: ZustandSet,
  get: ZustandGet,
  messageId: string,
) => {
  const intent = get().intents[messageId];
  if (!intent) {
    return;
  }
  set((state) => ({
    intents: {
      ...state.intents,
      [messageId]: { ...intent, expiresFrom: Date.now() },
    },
  }));
};

export const handleIsHiddenByIntent = (
  get: ZustandGet,
  messageId: string,
): boolean => isViewHiddenIntent(get().intents[messageId]);

export const handleIsLinkedByIntent = (
  get: ZustandGet,
  messageId: string,
): boolean => {
  const intent = get().intents[messageId];
  if (!intent) {
    return false;
  }
  return (
    intent.kind === CommunicationIntentKind.LINKED_TO_ENTRY &&
    isIntentActive(intent)
  );
};

export const handlePruneExpiredIntents = (set: ZustandSet, get: ZustandGet) => {
  const now = Date.now();
  const inactiveIds = Object.values(get().intents)
    .filter((intent) => !isIntentActive(intent, now))
    .map((intent) => intent.messageId);

  handleClearIntents(set, get, inactiveIds);
};
